import MapBehaviorSubject from '@adornis/base/rxjs-extension/reactives/map.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-empty-placeholder.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-grid.js';
import '@adornis/chemistry/elements/components/x-icon.js';
import { html, noChange, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { state } from 'lit/decorators/state.js';
import { repeat } from 'lit/directives/repeat.js';
import { switchMap } from 'rxjs';
import { getAnswerBySlotIDs } from '../../api/answer.js';
import { Answer } from '../../db/answer.js';
import '../components/ils-loader.js';
import { answerUploader, blobStore, currentExamDay } from '../global-state.js';

@customElement('ils-local-recordings')
export class ILSLocalRecordings extends ChemistryLitElement {
  override styles() {
    return [
      {
        ':host': {
          flex: '1',
          display: 'flex',
          flexDirection: 'column',
        },
      },
    ];
  }
  @state() public selectedDay = new RXController(this, currentExamDay);
  @state() public answers = new RXController(
    this,
    this.selectedDay.observable.pipe(
      switchMap(day =>
        (blobStore.getAllKeys() as Promise<string[]>).then(keys =>
          keys
            .map(key => {
              const [slotID, type] = key.split('_');
              return { slotID: slotID!, type: type as 'trainee' | 'combined' };
            })
            .filter(key => day?.slotIDs.includes(key.slotID)),
        ),
      ),
      switchMap(keys =>
        getAnswerBySlotIDs(keys.map(k => k.slotID))(Answer.allFields).then(answers =>
          keys.map(key => ({ answer: answers.find(a => a.slotID === key.slotID), key })),
        ),
      ),
    ),
  );
  private readonly _uploadsRunning = new MapBehaviorSubject<string, { running: boolean; error: string }>();
  @state() public uploadsRunning = new RXController(this, this._uploadsRunning);

  override render() {
    if (this.selectedDay.isLoading)
      return html`
        <x-flex center crossaxis-center ${css({ width: '100%', height: '100%' })}>
          <ils-loader></ils-loader>
        </x-flex>
      `;
    if (!this.selectedDay.value || !this.answers.value) return noChange;
    if (!this.answers.value?.length)
      return html`
        <x-empty-placeholder
          .text=${'Es wurden keine Aufnahmen für diesen Tag gefunden'}
          .img=${'close'}
          no-wrapper
        ></x-empty-placeholder>
      `;
    return html`
      <x-flex space="lg">
        <x-flex flex ${css({ overflowY: 'auto' })}>
          <x-grid columns="min-content min-content min-content min-content 1fr" ${css({ width: '100%' })}>
            ${repeat(
              this.answers.value,
              answer => answer.key.slotID + answer.key.type,
              answer => {
                const isUploaded =
                  (answer.key.type === 'combined' && answer.answer?.fileId !== 'pending') ||
                  (answer.key.type === 'trainee' && answer.answer?.onlyTraineeFileId !== 'pending');
                const ref = answer.key.slotID + '_' + answer.key.type;
                let upload = this.uploadsRunning.value?.get(ref);
                return html`
                  <x-flex center padding="sm">
                    <x-text> ${answer.key.slotID} </x-text>
                  </x-flex>
                  <x-flex center padding="sm">
                    <x-text> ${answer.key.type} </x-text>
                  </x-flex>
                  <x-flex center padding="sm">
                    <x-icon> ${isUploaded ? 'done' : 'report_problem'} </x-icon>
                  </x-flex>
                  <x-flex center padding="sm">
                    ${isUploaded
                      ? nothing
                      : html`
                          <x-button
                            ?disabled=${upload?.running}
                            @click=${async () => {
                              if (!upload) this._uploadsRunning.set(ref, (upload = { running: true, error: '' }));
                              try {
                                if (!answer.answer)
                                  answer.answer = new Answer({ slotID: answer.key.slotID, type: 'voice' });
                                const idbEntry = await blobStore.getBlob(ref);
                                const fileId = await answerUploader.enqueueFile(
                                  answer.key.slotID,
                                  answer.key.type,
                                  // @ts-expect-error blob exists
                                  idbEntry.blob,
                                );
                                if (answer.key.type === 'trainee') answer.answer.onlyTraineeFileId = fileId;
                                else answer.answer.fileId = fileId;
                                await answer.answer.save();
                              } catch (error: any) {
                                this._uploadsRunning.set(ref, { running: false, error: error.message });
                              } finally {
                                const error = this._uploadsRunning.get(ref)?.error;
                                this._uploadsRunning.set(ref, { running: false, error: error ?? '' });
                              }
                            }}
                          >
                            Hochladen
                          </x-button>
                        `}
                  </x-flex>
                  <x-flex center padding="sm">
                    ${upload?.error ? html` <x-text> ${upload.error} </x-text> ` : nothing}
                  </x-flex>
                `;
              },
            )}
          </x-grid>
        </x-flex>
      </x-flex>
    `;
  }
}
